import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { ApplicationRef, Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { UiButtonComponent, UIIconComponent } from '@design-system';
import { BottomSheetComponent } from '../../../../components/bottom-sheet/bottom-sheet.component';
import {
	DEFAULT_SYMBOL_DETAIL_EXPANSION,
	SYMBOL_DETAIL_PINNED_SECTION_LABEL,
	SYMBOL_DETAIL_SECTION_LABELS,
	SYMBOL_DETAIL_SECTIONS,
	SymbolDetailSectionExpansion,
	SymbolDetailSectionId
} from '../../../../models/symbol-detail-section.model';

interface SectionRow {
	id: SymbolDetailSectionId;
	label: string;
}

@Component({
	selector: 'app-symbol-detail-sections-bottom-sheet',
	imports: [BottomSheetComponent, CdkDropList, CdkDrag, UiButtonComponent, UIIconComponent],
	templateUrl: './symbol-detail-sections-bottom-sheet.component.html',
	styleUrl: './symbol-detail-sections-bottom-sheet.component.scss',
	host: {
		'data-cy': 'symbol-detail-sections-bottom-sheet'
	}
})
export class SymbolDetailSectionsBottomSheetComponent {
	readonly sectionOrder = input.required<readonly SymbolDetailSectionId[]>();

	readonly expanded = input.required<SymbolDetailSectionExpansion>();

	readonly orderChanged = output<SymbolDetailSectionId[]>();

	readonly resetRequested = output<void>();

	private readonly appRef = inject(ApplicationRef);

	private readonly sheet = viewChild<BottomSheetComponent>('sheet');

	private readonly draft = signal<SymbolDetailSectionId[]>([...SYMBOL_DETAIL_SECTIONS]);

	readonly pinnedLabel = SYMBOL_DETAIL_PINNED_SECTION_LABEL;

	readonly rows = computed<SectionRow[]>(() =>
		this.draft().map(id => ({ id, label: SYMBOL_DETAIL_SECTION_LABELS[id] }))
	);

	readonly isDefaultLayout = computed(
		() =>
			this.draft().every((id, index) => id === SYMBOL_DETAIL_SECTIONS[index]) &&
			SYMBOL_DETAIL_SECTIONS.every(id => this.expanded()[id] === DEFAULT_SYMBOL_DETAIL_EXPANSION[id])
	);

	open(): Promise<void> {
		this.draft.set([...this.sectionOrder()]);

		return this.sheet()?.open() ?? Promise.resolve();
	}

	close(): Promise<void> {
		return this.sheet()?.close() ?? Promise.resolve();
	}

	onDrop(event: CdkDragDrop<SectionRow[]>): void {
		if (event.previousIndex === event.currentIndex) {
			return;
		}

		const next = [...this.draft()];
		moveItemInArray(next, event.previousIndex, event.currentIndex);

		this.draft.set(next);
		this.orderChanged.emit(next);
		this.appRef.tick();
	}

	onResetToDefault(): void {
		if (this.isDefaultLayout()) {
			return;
		}

		this.draft.set([...SYMBOL_DETAIL_SECTIONS]);
		this.resetRequested.emit();
	}
}
